import React, { useState, useEffect } from 'react';
import {
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import OrderDetails from '../../components/admin/OrderDetails';
import axios from 'axios';

const AdminAgentOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [agents, setAgents] = useState([]);
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  useEffect(() => {
    fetchOrders();
    fetchAgents();
  }, [pagination.currentPage, statusFilter, agentFilter, searchTerm]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pagination.currentPage);
      params.append('limit', 100); // Get more to filter
      
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`/api/orders?${params.toString()}`);
      
      // Filter only orders created by field agents (orders with seller_id)
      const agentOrders = (response.data.orders || []).filter(order => {
        // Only check if seller_id exists
        const hasAgentId = order.seller_id != null;
        // Match by seller_id (which is the user_id of the field agent)
        const matchesAgent = !agentFilter || order.seller_id === agentFilter;
        return hasAgentId && matchesAgent;
      });
      
      // Debug: log first order with discounts
      const orderWithDiscount = agentOrders.find(o => getTotalDiscount(o) > 0);
      if (orderWithDiscount) {
      }
      
      setOrders(agentOrders);
      // Adjust pagination for filtered results
      setPagination({
        ...pagination,
        totalItems: agentOrders.length,
        totalPages: Math.ceil(agentOrders.length / pagination.itemsPerPage)
      });
    } catch (error) {
      console.error('Error fetching agent orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/api/admin/field-agents');
      const agentsList = response.data.agents || [];
      setAgents(agentsList);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  // Calculate total discount from order + items (like in AdminOrders.js)
  // Excludes tester and bonus items - they are not real discounts
  const getTotalDiscount = (order) => {
    // Discount from order level
    const orderDiscount = Math.abs(parseFloat(order.discount_amount || 0));
    
    // Discount from items - exclude testers and bonuses
    const itemsDiscount = (order.items || []).reduce((sum, item) => {
      // Skip tester and bonus items - they are not real discounts
      const isTesterOrBonus = item.notes && (item.notes.includes('טסטר') || item.notes.includes('בונוס'));
      if (isTesterOrBonus) {
        return sum;
      }
      return sum + Math.abs(parseFloat(item.discount_amount || 0));
    }, 0);
    
    return orderDiscount + itemsDiscount;
  };

  // Check if order has discounts (real discounts, not testers/bonuses)
  const hasDiscounts = (order) => {
    return getTotalDiscount(order) > 0;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'בהמתנה', className: 'bg-yellow-100 text-yellow-800', icon: ClockIcon },
      processing: { text: 'בעיבוד', className: 'bg-blue-100 text-blue-800', icon: ClockIcon },
      shipped: { text: 'נשלח', className: 'bg-purple-100 text-purple-800', icon: TruckIcon },
      delivered: { text: 'נמסר', className: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
      cancelled: { text: 'בוטל', className: 'bg-red-100 text-red-800', icon: XCircleIcon }
    };

    const badge = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800', icon: ClockIcon };
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {badge.text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
              <UserGroupIcon className="h-8 w-8 text-white" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                הזמנות סוכנים
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                כל ההזמנות שבוצעו על ידי סוכני השטח
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חפש הזמנה..."
                aria-label="חיפוש הזמנות"
                className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="סינון לפי סטטוס"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">כל הסטטוסים</option>
                <option value="pending">בהמתנה</option>
                <option value="processing">בעיבוד</option>
                <option value="shipped">נשלח</option>
                <option value="delivered">נמסר</option>
                <option value="cancelled">בוטל</option>
              </select>
            </div>

            {/* Agent Filter */}
            <div className="flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                aria-label="סינון לפי סוכן"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">כל הסוכנים</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.user_id || agent.id}>
                    {agent.user?.first_name || agent.first_name} {agent.user?.last_name || agent.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-end">
              <div className="text-center px-4 py-2 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <div className="text-xs text-purple-700">סה"כ הזמנות</div>
                <div className="text-lg font-bold text-purple-900">{orders.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">טוען הזמנות...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border-2 border-dashed border-gray-300">
            <UserGroupIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">אין הזמנות סוכנים</h3>
            <p className="text-gray-500">
              {statusFilter || agentFilter || searchTerm 
                ? 'לא נמצאו הזמנות התואמות את הסינון' 
                : 'טרם בוצעו הזמנות על ידי סוכנים'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                        סטטוס
                      </th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider w-24">
                        תאריך
                      </th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        סוכן
                      </th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                        לקוח/חברה
                      </th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider w-28">
                        סכום
                      </th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                        פעולות
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-transparent transition-all">
                        <td className="px-3 py-3">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-600">
                          {formatDate(order.createdAt || order.created_at)}
                        </td>
                        <td className="px-3 py-3">
                          {order.seller ? (
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-purple-700">
                                  {order.seller.first_name?.[0] || '?'}{order.seller.last_name?.[0] || '?'}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {order.seller.first_name} {order.seller.last_name}
                                </div>
                              </div>
                            </div>
                          ) : order.seller_id ? (
                            <div className="text-xs text-amber-600 truncate">
                              {order.seller_id.slice(0, 8)}...
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          {order.company ? (
                            <div className="flex items-center gap-1">
                              <BuildingOfficeIcon className="h-4 w-4 text-blue-500 flex-shrink-0" aria-hidden="true" />
                              <div className="text-sm font-medium text-gray-900 truncate">{order.company.name}</div>
                            </div>
                          ) : order.user ? (
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {order.user.first_name} {order.user.last_name}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <div>
                              <div className="text-sm font-bold text-primary-600">
                                {formatCurrency(order.total_amount)}
                              </div>
                              {hasDiscounts(order) && (
                                <div className="text-xs text-green-600 font-medium">
                                  -{formatCurrency(getTotalDiscount(order))}
                                </div>
                              )}
                            </div>
                            {hasDiscounts(order) && (
                              <span className="text-orange-500" title="הזמנה עם הנחות">⚠️</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderDetailsModal(true);
                            }}
                            aria-label={`צפה בהזמנה ${order.order_number}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all text-sm font-medium"
                          >
                            <EyeIcon className="h-4 w-4" aria-hidden="true" />
                            <span>צפה</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">מספר הזמנה</p>
                      <p className="text-lg font-bold text-gray-900">#{order.order_number}</p>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>

                  {/* Agent */}
                  {order.seller && (
                    <div className="bg-purple-50 rounded-lg p-3 mb-3 border border-purple-200">
                      <p className="text-xs text-purple-700 font-medium mb-1">סוכן שטח</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-purple-700">
                            {order.seller.first_name[0]}{order.seller.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {order.seller.first_name} {order.seller.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{order.seller.email}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Customer/Company */}
                  <div className="space-y-2 mb-3">
                    {order.company && (
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="h-4 w-4 text-blue-500" aria-hidden="true" />
                        <span className="text-sm font-medium text-gray-900">{order.company.name}</span>
                      </div>
                    )}
                    {order.user && (
                      <p className="text-xs text-gray-500">{order.user.email}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs text-gray-500">סכום</p>
                        {hasDiscounts(order) && (
                          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-md animate-pulse">
                            ⚠️ הנחה
                          </div>
                        )}
                      </div>
                      <p className="text-lg font-bold text-primary-600">
                        {formatCurrency(order.total_amount)}
                      </p>
                      {hasDiscounts(order) && (
                        <p className="text-xs text-orange-600 font-medium">
                          💸 חיסכון: ₪{getTotalDiscount(order).toFixed(2)}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(order.createdAt || order.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowOrderDetailsModal(true);
                      }}
                      aria-label={`צפה בהזמנה ${order.order_number}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                    >
                      <EyeIcon className="h-4 w-4" aria-hidden="true" />
                      <span>צפה</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetails
          isOpen={showOrderDetailsModal}
          onClose={() => {
            setShowOrderDetailsModal(false);
            setSelectedOrder(null);
          }}
          order={selectedOrder}
          onOrderUpdated={(updatedOrder) => {
            // Update order in the list
            setOrders(orders.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            setShowOrderDetailsModal(false);
            setSelectedOrder(null);
            fetchOrders(); // Refresh the list
          }}
        />
      )}
    </div>
  );
};

export default AdminAgentOrders;
